import { getRoomName } from '../base/conference/functions';
import { getDialOutStatusUrl, getDialOutUrl } from '../base/config/functions.web';
import { isAudioMuted, isVideoMutedByUser } from '../base/media/functions';
/**
 * Selector for the visibility of the 'join by phone' button.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isJoinByPhoneButtonVisible(state) {
    return Boolean(getDialOutUrl(state) && getDialOutStatusUrl(state));
}
/**
 * Selector for determining if the device status strip is visible or not.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isDeviceStatusVisible(state) {
    return !(isAudioMuted(state) && isVideoMutedByUser(state))
        && !state['features/base/config'].startSilent;
}
/**
 * Selector for determining if the display name is mandatory.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isDisplayNameRequired(state) {
    var _a, _b;
    return Boolean(((_a = state['features/prejoin']) === null || _a === void 0 ? void 0 : _a.isDisplayNameRequired)
        || ((_b = state['features/base/config']) === null || _b === void 0 ? void 0 : _b.requireDisplayName));
}
/**
 * Selector for determining if the prejoin display name field is visible.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isPrejoinDisplayNameVisible(state) {
    var _a;
    return !((_a = state['features/base/config'].prejoinConfig) === null || _a === void 0 ? void 0 : _a.hideDisplayName);
}
/**
 * Returns the text for the prejoin status bar.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getDeviceStatusText(state) {
    var _a;
    return (_a = state['features/prejoin']) === null || _a === void 0 ? void 0 : _a.deviceStatusText;
}
/**
 * Returns the type of the prejoin status bar: 'ok'|'warning'.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getDeviceStatusType(state) {
    var _a;
    return (_a = state['features/prejoin']) === null || _a === void 0 ? void 0 : _a.deviceStatusType;
}
/**
 * Returns the 'conferenceUrl' used for dialing out.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getDialOutConferenceUrl(state) {
    var _a;
    return `${getRoomName(state)}@${(_a = state['features/base/config'].hosts) === null || _a === void 0 ? void 0 : _a.muc}`;
}
/**
 * Selector for getting the dial out country.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {Object}
 */
export function getDialOutCountry(state) {
    return state['features/prejoin'].dialOutCountry;
}
/**
 * Selector for getting the dial out number (without prefix).
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getDialOutNumber(state) {
    return state['features/prejoin'].dialOutNumber;
}
/**
 * Selector for getting the dial out status while calling.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getDialOutStatus(state) {
    return state['features/prejoin'].dialOutStatus;
}
/**
 * Returns the full dial out number (containing country code and +).
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getFullDialOutNumber(state) {
    const dialOutNumber = getDialOutNumber(state);
    const country = getDialOutCountry(state);
    return `+${country.dialCode}${dialOutNumber}`;
}
/**
 * Selector for getting the error if any while creating streams.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {string}
 */
export function getRawError(state) {
    var _a;
    return (_a = state['features/prejoin']) === null || _a === void 0 ? void 0 : _a.rawError;
}
/**
 * Selector for getting the visibility state for the 'JoinByPhoneDialog'.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isJoinByPhoneDialogVisible(state) {
    var _a;
    return (_a = state['features/prejoin']) === null || _a === void 0 ? void 0 : _a.showJoinByPhoneDialog;
}
/**
 * Returns true if the prejoin page is enabled and no flag
 * to bypass showing the page is present.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isPrejoinPageVisible(state) {
    var _a, _b;
    return Boolean(navigator.product !== 'ReactNative'
        && ((_a = state['features/base/config'].prejoinConfig) === null || _a === void 0 ? void 0 : _a.enabled)
        && ((_b = state['features/prejoin']) === null || _b === void 0 ? void 0 : _b.showPrejoin)
        && !(state['features/base/config'].enableForcedReload && state['features/prejoin'].skipPrejoinOnReload));
}
/**
 * Returns true if we should auto-knock in case lobby is enabled for the room.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function shouldAutoKnock(state) {
    const { iAmRecorder, iAmSipGateway, autoKnockLobby, prejoinConfig } = state['features/base/config'];
    const { userSelectedSkipPrejoin } = state['features/base/settings'];
    const isPrejoinEnabled = prejoinConfig === null || prejoinConfig === void 0 ? void 0 : prejoinConfig.enabled;
    return Boolean(((isPrejoinEnabled && !userSelectedSkipPrejoin)
        || autoKnockLobby || (iAmRecorder && iAmSipGateway))
        && !state['features/lobby'].knocking);
}
